import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Inbox, Loader2 } from 'lucide-react';
import * as notifApi from '../api/notifications.api';

const POLL_MS = 30_000;
const MAX_ITEMS = 20;

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  // Lightweight badge poll: only the unread count, not the whole list.
  const pollUnread = useCallback(async () => {
    try {
      const { count } = await notifApi.unreadCount();
      setUnread(count || 0);
    } catch {
      // ignore — keep last known value
    }
  }, []);

  useEffect(() => {
    pollUnread();
    const id = setInterval(pollUnread, POLL_MS);
    return () => clearInterval(id);
  }, [pollUnread]);

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  async function loadList() {
    setLoading(true);
    try {
      const { items: list, unreadCount: count } = await notifApi.listMine({ limit: MAX_ITEMS });
      setItems(list || []);
      setUnread(count || 0);
    } catch {
      // Surface a minimal empty state on error.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  }

  async function onItemClick(n) {
    // Optimistic: mark read locally, then call API and navigate.
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
      notifApi.markRead(n._id).catch(() => {});
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  async function onMarkAll() {
    if (!unread) return;
    setItems((prev) => prev.map((x) => (x.readAt ? x : { ...x, readAt: new Date().toISOString() })));
    setUnread(0);
    try { await notifApi.markAllRead(); } catch { /* badge will resync on next poll */ }
  }

  return (
    <div className="profile-wrap" ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        aria-label="Notifications"
        onClick={toggle}
        style={{ position: 'relative' }}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="ping"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: 'var(--danger, #e11d48)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              display: 'inline-block',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="dropdown"
          role="menu"
          style={{
            width: 360,
            maxHeight: 480,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            right: 0,
            left: 'auto',
          }}
        >
          <div
            className="row between"
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--border)',
              alignItems: 'center',
            }}
          >
            <strong style={{ fontSize: 14 }}>Notifications</strong>
            <button
              type="button"
              onClick={onMarkAll}
              disabled={!unread}
              style={{
                background: 'transparent',
                border: 0,
                cursor: unread ? 'pointer' : 'default',
                color: unread ? 'var(--brand-700)' : 'var(--ink-400)',
                fontSize: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && (
              <div className="row" style={{ padding: 20, justifyContent: 'center', color: 'var(--ink-500)' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> <span style={{ marginLeft: 8 }}>Loading…</span>
              </div>
            )}

            {!loading && items.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-500)' }}>
                <Inbox size={20} />
                <div style={{ marginTop: 8, fontSize: 13 }}>You're all caught up.</div>
              </div>
            )}

            {!loading && items.map((n) => {
              const isUnread = !n.readAt;
              return (
                <button
                  key={n._id}
                  type="button"
                  onClick={() => onItemClick(n)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 0,
                    background: isUnread ? 'var(--brand-50, #f0f9ff)' : 'transparent',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    display: 'block',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'baseline',
                    }}
                  >
                    <strong style={{ fontSize: 13, color: 'var(--ink-900)' }}>{n.title}</strong>
                    <span style={{ fontSize: 11, color: 'var(--ink-500)', flexShrink: 0 }}>
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>
                  {n.body && (
                    <div style={{ marginTop: 2, fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.4 }}>
                      {n.body}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsBell;
